import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Select, 
  Modal, 
  message, 
  Space, 
  Typography, 
  Row,
  Col,
  Input,
  InputNumber,
  Form,
  Statistic,
  Alert,
  Tooltip,
  Switch,
  Tag,
  Divider
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { jurisdictionsService } from '../../services/jurisdictionsService';
import './Jurisdictions.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// US States options
const US_STATES_OPTIONS = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' }, 
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

function Jurisdictions() {
  const [loading, setLoading] = useState(false);
  const [hierarchyData, setHierarchyData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [availableCounties, setAvailableCounties] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  
  // Edit modal state
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedJurisdiction, setSelectedJurisdiction] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchHierarchyData();
  }, [selectedState, selectedCounty, selectedCity]);

  useEffect(() => {
    if (selectedState) {
      loadCounties(selectedState);
    } else {
      setAvailableCounties([]);
      setSelectedCounty(null);
    }
  }, [selectedState]);

  useEffect(() => {
    if (selectedState && selectedCounty) {
      loadCities(selectedState, selectedCounty);
    } else {
      setAvailableCities([]);
      setSelectedCity(null);
    }
  }, [selectedState, selectedCounty]);

  const fetchHierarchyData = async () => {
    setLoading(true);
    try {
      const filters = { country: 'US' };
      if (selectedState) filters.state = selectedState;
      if (selectedCounty) filters.county = selectedCounty;
      if (selectedCity) filters.city = selectedCity;

      const data = await jurisdictionsService.getAll(filters);
      
      // Apply search filter if needed
      let filteredData = data;
      if (searchValue) {
        filteredData = applySearchFilter(data, searchValue);
      }
      
      // Build hierarchy from flat data
      const hierarchy = buildJurisdictionHierarchy(filteredData);
      setHierarchyData(hierarchy);
    } catch (error) {
      console.error('Error fetching hierarchy data:', error);
      message.error('Failed to fetch jurisdiction data');
    } finally {
      setLoading(false);
    }
  };

  const loadCounties = async (state) => {
    try {
      const allData = await jurisdictionsService.getAll({ country: 'US', state });
      const counties = [...new Set(allData.map(j => j.county).filter(Boolean))].sort();
      setAvailableCounties(counties);
    } catch (error) {
      console.error('Error loading counties:', error);
    }
  };

  const loadCities = async (state, county) => {
    try {
      const allData = await jurisdictionsService.getAll({ country: 'US', state });
      const cities = [...new Set(allData.filter(j => j.county === county).map(j => j.city).filter(Boolean))].sort();
      setAvailableCities(cities);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const applySearchFilter = (data, searchTerm) => {
    const term = searchTerm.toLowerCase();
    return data.filter(jurisdiction => {
      return [
        jurisdiction.state,
        jurisdiction.county,
        jurisdiction.city,
        jurisdiction.zipCode,
        ...(jurisdiction.zipCodes || [])
      ].some(field => field && field.toLowerCase().includes(term));
    });
  };

  const buildJurisdictionHierarchy = (jurisdictions) => {
    const hierarchy = new Map(); // state -> counties -> cities -> jurisdictions
    
    jurisdictions.forEach(jurisdiction => {
      const { state, county, city, zipCode } = jurisdiction;
      const stateKey = state || 'Unknown State';
      const countyKey = county || 'Unknown County';
      const cityKey = city || 'Unknown City';
      
      // Initialize state
      if (!hierarchy.has(stateKey)) {
        hierarchy.set(stateKey, {
          id: `state_${stateKey}`,
          name: stateKey,
          type: 'state',
          state: stateKey,
          counties: new Map(),
          allJurisdictions: []
        });
      }
      
      const stateData = hierarchy.get(stateKey);
      
      // Initialize county
      if (!stateData.counties.has(countyKey)) {
        stateData.counties.set(countyKey, {
          id: `county_${stateKey}_${countyKey}`,
          name: countyKey,
          type: 'county',
          state: stateKey,
          county: countyKey,
          cities: new Map(),
          allJurisdictions: []
        });
      }
      
      const countyData = stateData.counties.get(countyKey);
      
      // Initialize city
      if (!countyData.cities.has(cityKey)) {
        countyData.cities.set(cityKey, {
          id: `city_${stateKey}_${countyKey}_${cityKey}`,
          name: cityKey,
          type: 'city',
          state: stateKey,
          county: countyKey,
          city: cityKey,
          zipJurisdictions: [],
          cityLevelJurisdiction: null
        });
      }
      
      const cityData = countyData.cities.get(cityKey);
      
      // Categorize jurisdictions: ZIP-level vs city-level
      const jurisdictionWithType = {
        ...jurisdiction,
        type: 'jurisdiction'
      };
      
      // Track all jurisdictions for statistics
      stateData.allJurisdictions.push(jurisdictionWithType);
      countyData.allJurisdictions.push(jurisdictionWithType);
      
      // Separate ZIP-level from city-level jurisdictions
      if (zipCode) {
        // This is a ZIP-level jurisdiction
        cityData.zipJurisdictions.push(jurisdictionWithType);
      } else {
        // This is a city-level jurisdiction (no ZIP code)
        cityData.cityLevelJurisdiction = jurisdictionWithType;
      }
    });
    
    // Convert to array structure with corrected statistics
    return Array.from(hierarchy.values()).map(state => {
      const leafJurisdictions = state.allJurisdictions.filter(j => j.zipCode);
      const cityLevelJurisdictions = state.allJurisdictions.filter(j => !j.zipCode);
      
      return {
        ...state,
        totalJurisdictions: leafJurisdictions.length,
        totalZipCodes: leafJurisdictions.length,
        children: Array.from(state.counties.values()).map(county => {
          const countyLeafJurisdictions = county.allJurisdictions.filter(j => j.zipCode);
          const countyCityLevelJurisdictions = county.allJurisdictions.filter(j => !j.zipCode);
          
          return {
            ...county,
            totalJurisdictions: countyLeafJurisdictions.length,
            totalZipCodes: countyLeafJurisdictions.length,
            children: Array.from(county.cities.values())
              .filter(city => {
                // Filter out Unknown City entries that only contain ZIP-level jurisdictions
                if (city.name === 'Unknown City' && city.zipJurisdictions.length > 0 && !city.cityLevelJurisdiction) {
                  return false;
                }
                // Keep all other cities
                return true;
              })
              .map(city => {
                const cityChildren = [];
                
                // Add ZIP-level jurisdictions as children
                city.zipJurisdictions.forEach(zipJurisdiction => {
                  cityChildren.push(zipJurisdiction);
                });
                
                // Only show city-level jurisdiction if no ZIP codes exist AND it's not 'Unknown City'
                if (city.zipJurisdictions.length === 0 && city.cityLevelJurisdiction && city.name !== 'Unknown City') {
                  cityChildren.push({
                    ...city.cityLevelJurisdiction,
                    displayName: `${city.name} (City Level)`
                  });
                }
                
                return {
                  ...city,
                  totalJurisdictions: city.zipJurisdictions.length || (city.cityLevelJurisdiction ? 1 : 0),
                  totalZipCodes: city.zipJurisdictions.length,
                  children: cityChildren
                };
              })
          };
        })
      };
    });
  };

  const handleRowExpand = (expanded, record) => {
    const key = record.id;
    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, key]);
    } else {
      setExpandedRowKeys(expandedRowKeys.filter(k => k !== key));
    }
  };

  const handleEditJurisdiction = (jurisdiction) => {
    setIsEditing(true);
    setSelectedJurisdiction(jurisdiction);
    form.setFieldsValue({
      state: jurisdiction.state,
      county: jurisdiction.county,
      city: jurisdiction.city,
      zipCodes: jurisdiction.zipCodes?.join(', '),
      districts: jurisdiction.districts?.join(', '),
      taxAuthorityName: jurisdiction.taxAuthority?.name,
      taxAuthorityContact: jurisdiction.taxAuthority?.contactInfo,
      taxAuthorityWebsite: jurisdiction.taxAuthority?.website,
      population: jurisdiction.population,
      areaSquareMiles: jurisdiction.areaSquareMiles,
      isActive: jurisdiction.isActive
    });
    setEditModalVisible(true);
  };

  const handleDeleteJurisdiction = async (jurisdictionId) => {
    try {
      await jurisdictionsService.deleteJurisdiction(jurisdictionId);
      message.success('Jurisdiction deleted successfully');
      fetchHierarchyData();
    } catch (error) {
      console.error('Error deleting jurisdiction:', error);
      message.error('Failed to delete jurisdiction');
    }
  };

  const handleAddJurisdiction = () => {
    setIsEditing(false);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      country: 'US'
    });
    setEditModalVisible(true);
  };

  const handleSaveJurisdiction = async (values) => {
    try {
      const jurisdictionData = {
        ...values,
        country: 'US',
        zipCodes: values.zipCodes ? values.zipCodes.split(',').map(zip => zip.trim()) : [],
        districts: values.districts ? values.districts.split(',').map(district => district.trim()) : [],
        taxAuthority: {
          name: values.taxAuthorityName || '',
          contactInfo: values.taxAuthorityContact || '',
          website: values.taxAuthorityWebsite || ''
        },
        isActive: values.isActive !== false,
        source: 'manual'
      };

      if (isEditing) {
        await jurisdictionsService.updateJurisdiction(selectedJurisdiction.id, jurisdictionData);
        message.success('Jurisdiction updated successfully');
      } else {
        await jurisdictionsService.createJurisdiction(jurisdictionData);
        message.success('Jurisdiction created successfully');
      }

      setEditModalVisible(false);
      form.resetFields();
      setSelectedJurisdiction(null);
      fetchHierarchyData();
    } catch (error) {
      console.error('Error saving jurisdiction:', error);
      message.error('Failed to save jurisdiction');
    }
  };

  // Build expandable table structure
  const buildTableData = () => {
    return hierarchyData.map(state => ({
      key: state.id,
      id: state.id,
      type: 'state',
      name: state.name,
      state: state.state,
      totalJurisdictions: state.totalJurisdictions,
      totalZipCodes: state.totalZipCodes,
      children: state.children.map(county => ({
        key: county.id,
        id: county.id,
        type: 'county',
        name: county.name,
        state: county.state,
        county: county.county,
        totalJurisdictions: county.totalJurisdictions,
        totalZipCodes: county.totalZipCodes,
        children: county.children
          .filter(city => {
            // Filter out Unknown City entries that only have ZIP-level jurisdictions
            if (city.name === 'Unknown City' && city.zipJurisdictions && city.zipJurisdictions.length > 0) {
              return false;
            }
            // Keep cities with actual names or city-level jurisdictions
            return city.name !== 'Unknown City' || city.children.length > 0;
          })
          .map(city => ({
            key: city.id,
            id: city.id,
            type: 'city',
            name: city.name,
            state: city.state,
            county: city.county,
            city: city.city,
            totalJurisdictions: city.totalJurisdictions,
            totalZipCodes: city.totalZipCodes,
            children: city.children.map(jurisdiction => ({
              key: jurisdiction.id,
              id: jurisdiction.id,
              type: 'jurisdiction',
              ...jurisdiction
            }))
          }))
      }))
    }));
  };

  const columns = [
    {
      title: 'Location',
      dataIndex: 'name',
      key: 'location',
      width: 300,
      render: (text, record) => {
        const indent = record.type === 'county' ? 20 : record.type === 'city' ? 40 : record.type === 'jurisdiction' ? 60 : 0;
        
        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            paddingLeft: indent
          }}>
            <EnvironmentOutlined style={{ marginRight: 8, color: '#D4AF37' }} />
            <div>
              <Text strong={record.type !== 'jurisdiction'}>
                {record.type === 'jurisdiction' ? (
                  record.displayName || 
                  `${record.city || record.county || record.state} (${record.zipCodes?.join(', ') || 'No ZIP'})`
                ) : text}
              </Text>
              {record.type !== 'jurisdiction' && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {record.totalJurisdictions} jurisdiction{record.totalJurisdictions !== 1 ? 's' : ''} • {record.totalZipCodes} ZIP codes
                </div>
              )}
              {record.type === 'jurisdiction' && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  ID: {record.id}
                </div>
              )}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Tax Authority',
      dataIndex: 'taxAuthority',
      key: 'taxAuthority',
      width: 250,
      render: (taxAuthority, record) => {
        if (record.type !== 'jurisdiction') return null;
        
        return (
          <div>
            <Text strong style={{ fontSize: '13px' }}>{taxAuthority?.name || 'Not specified'}</Text>
            {taxAuthority?.contactInfo && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                📞 {taxAuthority.contactInfo}
              </div>
            )}
            {taxAuthority?.website && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                🌐 <a href={taxAuthority.website} target="_blank" rel="noopener noreferrer">
                  Website
                </a>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Status & Source',
      key: 'status',
      width: 150,
      render: (_, record) => {
        if (record.type !== 'jurisdiction') return null;
        
        return (
          <div>
            <Tag color={record.isActive ? 'green' : 'red'}>
              {record.isActive ? 'Active' : 'Inactive'}
            </Tag>
            <br />
            <Tag color={record.source === 'manual' ? 'blue' : 'orange'} style={{ marginTop: 4 }}>
              {record.source}
            </Tag>
          </div>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => {
        if (record.type !== 'jurisdiction') return null;
        
        return (
          <Space size="small">
            <Tooltip title="Edit Jurisdiction">
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEditJurisdiction(record)}
                size="small"
              />
            </Tooltip>
            <Tooltip title="Delete Jurisdiction">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteJurisdiction(record.id)}
                size="small"
              />
            </Tooltip>
          </Space>
        );
      }
    }
  ];

  const tableData = buildTableData();

  return (
    <div className="jurisdictions">
      <div className="jurisdictions-header">
        <Title level={2}>Jurisdictions Management</Title>
        <Text type="secondary">
          Manage tax jurisdictions and geographic boundaries with hierarchical organization
        </Text>
      </div>

      {/* Cascading Filters */}
      <Card className="filters-card" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={4}>
            <Select
              placeholder="Select State"
              value={selectedState}
              onChange={setSelectedState}
              allowClear
              style={{ width: '100%' }}
            >
              {US_STATES_OPTIONS.map(state => (
                <Option key={state.code} value={state.code}>
                  {state.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Select County"
              value={selectedCounty}
              onChange={setSelectedCounty}
              allowClear
              disabled={!selectedState}
              style={{ width: '100%' }}
            >
              {availableCounties.map(county => (
                <Option key={county} value={county}>
                  {county}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Select City"
              value={selectedCity}
              onChange={setSelectedCity}
              allowClear
              disabled={!selectedCounty}
              style={{ width: '100%' }}
            >
              {availableCities.map(city => (
                <Option key={city} value={city}>
                  {city}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Input
              placeholder="Search jurisdictions..."
              prefix={<SearchOutlined />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onPressEnter={fetchHierarchyData}
            />
          </Col>
          <Col span={8}>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddJurisdiction}
              >
                Add Jurisdiction
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchHierarchyData}
                loading={loading}
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Summary Statistics */}
      {selectedState && (
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic 
                title="Total States" 
                value={hierarchyData.length}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Total Counties" 
                value={hierarchyData.reduce((sum, state) => sum + state.children.length, 0)}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Total Cities" 
                value={hierarchyData.reduce((sum, state) => 
                  sum + state.children.reduce((countySum, county) => countySum + county.children.length, 0), 0
                )}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Total Jurisdictions" 
                value={hierarchyData.reduce((sum, state) => sum + state.totalJurisdictions, 0)}
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Main Table */}
      <Card className="jurisdictions-table-card">
        {!selectedState ? (
          <Alert
            message="Select a state to view jurisdiction data"
            description="Use the state filter above to browse jurisdictions by state, county, and city."
            type="info"
            showIcon
            style={{ margin: '20px 0' }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={tableData}
            loading={loading}
            expandable={{
              expandedRowKeys,
              onExpand: handleRowExpand,
              expandRowByClick: true,
              rowExpandable: (record) => record.type !== 'jurisdiction',
            }}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} items`
            }}
            size="middle"
            scroll={{ x: true }}
            rowClassName={(record) => {
              // Define brighter background colors for hierarchy levels
              switch (record.type) {
                case 'state': return 'hierarchy-state-row';
                case 'county': return 'hierarchy-county-row';
                case 'city': return 'hierarchy-city-row';
                case 'jurisdiction': return 'hierarchy-jurisdiction-row';
                default: return '';
              }
            }}
          />
        )}
      </Card>

      {/* Add/Edit Jurisdiction Modal */}
      <Modal
        title={isEditing ? 'Edit Jurisdiction' : 'Add Jurisdiction'}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={() => form.submit()}
        okText={isEditing ? 'Update' : 'Create'}
        width={800}
        style={{ top: 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveJurisdiction}
        >
          <Alert
            message="Jurisdiction Configuration"
            description="Define geographic boundaries for tax calculation. More specific jurisdictions (city level) take precedence over broader ones (state level)."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="state"
                label="State"
                rules={[{ required: true, message: 'Please select state' }]}
              >
                <Select placeholder="Select state">
                  {US_STATES_OPTIONS.map(state => (
                    <Option key={state.code} value={state.code}>
                      {state.name} ({state.code})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="county"
                label="County (Optional)"
              >
                <Input placeholder="Enter county name" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="city"
                label="City (Optional)"
              >
                <Input placeholder="Enter city name" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="zipCodes"
                label="ZIP Codes"
                help="Comma-separated list of ZIP codes"
              >
                <TextArea 
                  rows={2}
                  placeholder="90210, 90211, 90212"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="districts"
                label="Special Districts"
                help="Comma-separated list of special tax districts"
              >
                <TextArea 
                  rows={2}
                  placeholder="Downtown District, Airport District"
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Tax Authority Information</Divider>

          <Form.Item
            name="taxAuthorityName"
            label="Tax Authority Name"
          >
            <Input placeholder="Department of Revenue" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="taxAuthorityContact"
                label="Contact Information"
              >
                <Input placeholder="Phone number or email" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="taxAuthorityWebsite"
                label="Website"
              >
                <Input placeholder="https://example.gov" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Geographic Information</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="population"
                label="Population"
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  placeholder="Population count"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="areaSquareMiles"
                label="Area (Square Miles)"
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  placeholder="Area in square miles"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="isActive"
            label="Status"
            valuePropName="checked"
          >
            <Switch 
              checkedChildren="Active" 
              unCheckedChildren="Inactive" 
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Jurisdictions;